import React, { useState } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Building2, ChevronDown, Plus, Pencil, Trash2, Check } from 'lucide-react';

const SubaccountSwitcher = () => {
  const { user, activeAccountId, setActiveAccountId, subaccounts, refreshSubaccounts, updateUserMetadata } = useAuth();
  const { toast } = useToast();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [formData, setFormData] = useState({ company_name: '', document: '', phone: '', contact_name: '' });
  const [renameName, setRenameName] = useState('');
  const [loading, setLoading] = useState(false);

  // Determine current active account name
  const isActiveMain = activeAccountId === user?.id;
  const mainAccountName = user?.user_metadata?.full_name || 'Conta Principal';
  const activeSubaccount = subaccounts.find(s => s.id === activeAccountId);
  
  const currentName = isActiveMain ? mainAccountName : (activeSubaccount?.company_name || 'Conta Desconhecida');

  const handleSwitch = (id) => {
    setActiveAccountId(id);
    localStorage.setItem('conciliador_active_account', id);
    // Reload window to ensure all states are cleared and re-fetched cleanly
    window.location.reload();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.company_name) {
      toast({ title: 'Nome obrigatório', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from('subaccounts').insert({
        parent_user_id: user.id,
        company_name: formData.company_name,
        document: formData.document,
        phone: formData.phone,
        contact_name: formData.contact_name
      });
      if (error) throw error;
      toast({ title: 'Conta criada com sucesso!' });
      setIsCreateOpen(false);
      setFormData({ company_name: '', document: '', phone: '', contact_name: '' });
      await refreshSubaccounts();
    } catch (error) {
      toast({ title: 'Erro ao criar conta', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openRename = () => {
    setRenameName(currentName);
    setIsRenameOpen(true);
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameName) return;
    setLoading(true);
    try {
      if (isActiveMain) {
        await updateUserMetadata({ full_name: renameName });
        const { error } = await supabase.from('user_subscriptions').update({ user_name: renameName }).eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('subaccounts').update({ company_name: renameName }).eq('id', activeAccountId);
        if (error) throw error;
        await refreshSubaccounts();
      }
      toast({ title: 'Conta renomeada com sucesso!' });
      setIsRenameOpen(false);
    } catch (error) {
      toast({ title: 'Erro ao renomear', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (isActiveMain) {
        toast({ title: 'Não permitido', description: 'Para excluir a conta principal, utilize a Gestão de Usuários ou contate o suporte.', variant: 'destructive' });
        setLoading(false);
        return;
      }
      
      const { error } = await supabase.from('subaccounts').delete().eq('id', activeAccountId);
      if (error) throw error;
      
      toast({ title: 'Conta excluída com sucesso!' });
      setIsDeleteOpen(false);
      // Switch back to main account
      handleSwitch(user.id);
    } catch (error) {
      toast({ title: 'Erro ao excluir', description: error.message, variant: 'destructive' });
      setLoading(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2 border-white/20 bg-slate-900/50 hover:bg-slate-800 text-white min-w-[200px] justify-between">
            <div className="flex items-center gap-2 truncate">
              <Building2 className="w-4 h-4 text-emerald-400" />
              <span className="truncate">{currentName}</span>
            </div>
            <ChevronDown className="w-4 h-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[280px] bg-slate-900 border-slate-700 text-slate-200">
          <DropdownMenuLabel className="text-xs text-slate-400">Alternar Conta</DropdownMenuLabel>
          
          <DropdownMenuItem 
            className="flex items-center justify-between cursor-pointer focus:bg-slate-800"
            onClick={() => handleSwitch(user.id)}
          >
            <span className={isActiveMain ? "font-bold text-white" : ""}>{mainAccountName} (Principal)</span>
            {isActiveMain && <Check className="w-4 h-4 text-emerald-400" />}
          </DropdownMenuItem>

          {subaccounts.map(sub => (
            <DropdownMenuItem 
              key={sub.id} 
              className="flex items-center justify-between cursor-pointer focus:bg-slate-800"
              onClick={() => handleSwitch(sub.id)}
            >
              <span className={activeAccountId === sub.id ? "font-bold text-white truncate" : "truncate"}>{sub.company_name}</span>
              {activeAccountId === sub.id && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 ml-2" />}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator className="bg-slate-700" />
          
          <DropdownMenuItem className="cursor-pointer text-emerald-400 focus:bg-slate-800 focus:text-emerald-300" onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            Criar Nova Conta
          </DropdownMenuItem>
          
          <DropdownMenuSeparator className="bg-slate-700" />
          
          <DropdownMenuItem className="cursor-pointer focus:bg-slate-800" onClick={openRename}>
            <Pencil className="mr-2 w-4 h-4" />
            Renomear Conta Atual
          </DropdownMenuItem>
          
          {!isActiveMain && (
            <DropdownMenuItem className="cursor-pointer text-red-400 focus:bg-slate-800 focus:text-red-300" onClick={() => setIsDeleteOpen(true)}>
              <Trash2 className="mr-2 w-4 h-4" />
              Excluir Conta Atual
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Modal Criar */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-slate-900 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle>Criar Nova Conta</DialogTitle>
            <DialogDescription className="text-slate-400">
              Crie uma subconta independente. O faturamento e período de uso serão gerados separadamente da sua conta principal.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-sm text-slate-300">Nome da Empresa *</label>
              <Input 
                required 
                value={formData.company_name} 
                onChange={e => setFormData({...formData, company_name: e.target.value})} 
                className="bg-slate-800 border-slate-700 text-white" 
                placeholder="Ex: Padaria Central"
              />
            </div>
            <div>
              <label className="text-sm text-slate-300">CPF ou CNPJ</label>
              <Input 
                value={formData.document} 
                onChange={e => setFormData({...formData, document: e.target.value})} 
                className="bg-slate-800 border-slate-700 text-white" 
                placeholder="Apenas números"
              />
            </div>
            <div>
              <label className="text-sm text-slate-300">Telefone</label>
              <Input 
                value={formData.phone} 
                onChange={e => setFormData({...formData, phone: e.target.value})} 
                className="bg-slate-800 border-slate-700 text-white" 
              />
            </div>
            <div>
              <label className="text-sm text-slate-300">Contato (Nome do responsável)</label>
              <Input 
                value={formData.contact_name} 
                onChange={e => setFormData({...formData, contact_name: e.target.value})} 
                className="bg-slate-800 border-slate-700 text-white" 
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsCreateOpen(false)} disabled={loading}>Cancelar</Button>
              <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                {loading ? 'Criando...' : 'Criar Conta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Renomear */}
      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent className="bg-slate-900 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle>Renomear Conta</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="space-y-4">
            <div>
              <label className="text-sm text-slate-300">Novo Nome</label>
              <Input 
                required 
                value={renameName} 
                onChange={e => setRenameName(e.target.value)} 
                className="bg-slate-800 border-slate-700 text-white" 
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsRenameOpen(false)} disabled={loading}>Cancelar</Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-500 text-white">
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Excluir */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-slate-900 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-red-400">Excluir Conta</DialogTitle>
            <DialogDescription className="text-slate-300 pt-2">
              Você está prestes a excluir permanentemente a conta <strong className="text-white">{currentName}</strong>.<br/><br/>
              <span className="text-red-400 font-bold">ATENÇÃO:</span> Todos os dados de vendas, recebimentos e conciliações vinculados a esta conta serão APAGADOS e não poderão ser recuperados.<br/><br/>
              Deseja realmente prosseguir?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button type="button" variant="ghost" onClick={() => setIsDeleteOpen(false)} disabled={loading}>Cancelar</Button>
            <Button type="button" onClick={handleDelete} disabled={loading} className="bg-red-600 hover:bg-red-500 text-white">
              {loading ? 'Excluindo...' : 'Sim, Excluir Definitivamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SubaccountSwitcher;
